import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Mail, 
  Send,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Loader2,
  HelpCircle,
  Users,
  Hammer,
  ShoppingBag,
  Building
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Contact reasons
const CONTACT_REASONS = [
  { value: 'general', label: 'Informazioni generali', icon: HelpCircle },
  { value: 'order', label: 'Problema con un ordine', icon: ShoppingBag },
  { value: 'crafter', label: 'Voglio diventare artigiano', icon: Hammer },
  { value: 'partnership', label: 'Proposte di partnership', icon: Users },
  { value: 'press', label: 'Stampa e media', icon: Building },
  { value: 'other', label: 'Altro', icon: MessageSquare }
];

// Contact info
const CONTACT_INFO = [
  {
    icon: Mail,
    title: 'Email',
    value: 'info@rarifex.com',
    description: 'Rispondiamo entro 24-48 ore lavorative',
    href: 'mailto:info@rarifex.com'
  }
];

// FAQ quick links
const QUICK_HELP = [
  { label: 'Come funziona Rarifex?', href: '/come-funziona' },
  { label: 'Come acquistare?', href: '/come-acquistare' },
  { label: 'Resi e rimborsi', href: '/resi-rimborsi' },
  { label: 'Pagamenti sicuri', href: '/pagamenti' }
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    reason: 'general',
    subject: '',
    message: '',
    orderNumber: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Errore nell\'invio del messaggio');
      }

      setIsSubmitted(true);
      toast.success('Messaggio inviato con successo!');
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Errore nell\'invio del messaggio. Riprova più tardi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <Helmet>
          <title>Messaggio Inviato | Contatti Rarifex</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-green-50/30 flex items-center justify-center px-4">
          <Card className="max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Messaggio inviato!</h1>
            <p className="text-slate-600 mb-6">
              Grazie per averci contattato. Ti risponderemo entro 24-48 ore lavorative 
              all'indirizzo email che hai indicato.
            </p>
            <Button 
              onClick={() => {
                setIsSubmitted(false);
                setFormData({
                  name: '',
                  email: '',
                  reason: 'general',
                  subject: '',
                  message: '',
                  orderNumber: ''
                });
              }}
              variant="outline"
            >
              Invia un altro messaggio
            </Button>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Contatti | Rarifex - Assistenza e Supporto</title>
        <meta name="description" content="Contatta il team Rarifex per assistenza, informazioni o proposte di partnership. Rispondiamo entro 24-48 ore lavorative." />
        <meta name="keywords" content="contatti rarifex, assistenza rarifex, supporto clienti, email rarifex, contattare rarifex" />
        <link rel="canonical" href="https://www.rarifex.com/contatti" />
        <meta property="og:title" content="Contatti | Rarifex - Assistenza e Supporto" />
        <meta property="og:description" content="Contatta il team Rarifex per assistenza, informazioni o proposte di partnership." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/contatti" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#008080]/10 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-[#008080]/10 text-[#008080] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <MessageSquare className="w-4 h-4" />
                Contattaci
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight mb-6">
                Come possiamo aiutarti?
              </h1>
              
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Siamo qui per rispondere alle tue domande. Compila il form o scrivici direttamente.
              </p>
            </div>
          </div>
        </section>

        {/* MAIN CONTENT */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-3 gap-12">
                
                {/* CONTACT FORM */}
                <div className="lg:col-span-2">
                  <Card className="p-6 md:p-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-6">Inviaci un messaggio</h2>
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Name and Email row */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Nome completo *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Mario Rossi"
                            required
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="mario@esempio.com"
                            required
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>

                      {/* Reason */}
                      <div className="space-y-2">
                        <Label htmlFor="reason">Motivo del contatto</Label>
                        <select
                          id="reason"
                          name="reason"
                          value={formData.reason}
                          onChange={handleChange}
                          className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#008080] focus:border-transparent"
                          data-testid="contact-reason-select"
                        >
                          {CONTACT_REASONS.map(reason => (
                            <option key={reason.value} value={reason.value}>
                              {reason.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Order number (conditional) */}
                      {formData.reason === 'order' && (
                        <div className="space-y-2">
                          <Label htmlFor="orderNumber">Numero ordine</Label>
                          <Input
                            id="orderNumber"
                            name="orderNumber"
                            value={formData.orderNumber}
                            onChange={handleChange}
                            placeholder="RF-2025-XXXX"
                            data-testid="contact-order-input"
                          />
                        </div>
                      )}

                      {/* Subject */}
                      <div className="space-y-2">
                        <Label htmlFor="subject">Oggetto</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="Di cosa vuoi parlarci?"
                          data-testid="contact-subject-input"
                        />
                      </div>

                      {/* Message */}
                      <div className="space-y-2">
                        <Label htmlFor="message">Messaggio *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Scrivi il tuo messaggio..."
                          rows={6}
                          required
                          data-testid="contact-message-input"
                        />
                      </div>

                      {/* Submit */}
                      <Button 
                        type="submit" 
                        className="w-full bg-[#008080] hover:bg-[#006666]"
                        disabled={isSubmitting}
                        data-testid="contact-submit-btn"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Invio in corso...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Invia Messaggio
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-slate-500 text-center">
                        Inviando questo form accetti la nostra{' '}
                        <a href="/privacy" className="text-[#008080] hover:underline">Privacy Policy</a>.
                        I campi con * sono obbligatori.
                      </p>
                    </form>
                  </Card>
                </div>

                {/* SIDEBAR */}
                <div className="space-y-6">
                  {/* Contact info cards */}
                  {CONTACT_INFO.map((info, index) => (
                    <Card key={index} className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-[#008080]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <info.icon className="w-5 h-5 text-[#008080]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{info.title}</h3>
                          {info.href ? (
                            <a 
                              href={info.href} 
                              className="text-[#008080] hover:underline font-medium"
                            >
                              {info.value}
                            </a>
                          ) : (
                            <p className="font-medium text-slate-700">{info.value}</p>
                          )}
                          <p className="text-sm text-slate-500 mt-1">{info.description}</p>
                        </div>
                      </div>
                    </Card>
                  ))}

                  {/* Quick help */}
                  <Card className="p-5 bg-slate-50">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <HelpCircle className="w-4 h-4" />
                      Risposte rapide
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                      Prima di scriverci, forse trovi già la risposta:
                    </p>
                    <ul className="space-y-2">
                      {QUICK_HELP.map((item, index) => (
                        <li key={index}>
                          <a 
                            href={item.href}
                            className="text-sm text-[#008080] hover:underline flex items-center gap-1"
                          >
                            → {item.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </Card>

                  {/* Response time */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-slate-900 mb-1">Tempi di risposta</h4>
                        <p className="text-sm text-slate-600">
                          Rispondiamo generalmente entro <strong>24-48 ore lavorative</strong>. 
                          Per urgenze relative a ordini in corso, indica il numero ordine nel messaggio.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
